import { useCallback, useMemo, useRef, useSyncExternalStore } from 'react';
import { EditorState, UndoableState } from '../state/types';
import { filterSelectedItemstoOnlyReturnExistingItems } from './filter-selected-items-for-only-existing-items';

const MAX_HISTORY_SIZE = 50;

export interface EditorHistorySnapshot {
  undoableState: UndoableState;
  brandTemplateId: EditorState['brandTemplateId'];
  editorAppliedAccountBrandTemplateId: EditorState['editorAppliedAccountBrandTemplateId'];
}

export interface EditorHistoryState {
  entries: EditorHistorySnapshot[];
  index: number;
}

export const createEditorHistorySnapshot = (
  state: Pick<
    EditorState,
    'undoableState' | 'brandTemplateId' | 'editorAppliedAccountBrandTemplateId'
  >,
): EditorHistorySnapshot => ({
  undoableState: state.undoableState,
  brandTemplateId: state.brandTemplateId,
  editorAppliedAccountBrandTemplateId:
    state.editorAppliedAccountBrandTemplateId,
});

const isSameEditorHistorySnapshot = (
  left: EditorHistorySnapshot,
  right: EditorHistorySnapshot,
) =>
  left.undoableState === right.undoableState &&
  left.brandTemplateId === right.brandTemplateId &&
  left.editorAppliedAccountBrandTemplateId ===
    right.editorAppliedAccountBrandTemplateId;

export const pushEditorHistorySnapshot = (
  history: EditorHistoryState,
  snapshot: EditorHistorySnapshot,
): EditorHistoryState => {
  const currentEntry = history.entries[history.index];
  if (currentEntry && isSameEditorHistorySnapshot(currentEntry, snapshot)) {
    return history;
  }

  const truncated = history.entries.slice(0, history.index + 1);
  let entries = [...truncated, snapshot];
  if (entries.length > MAX_HISTORY_SIZE) {
    entries = entries.slice(entries.length - MAX_HISTORY_SIZE);
  }

  return {
    entries,
    index: entries.length - 1,
  };
};

export const moveEditorHistoryCursor = (
  history: EditorHistoryState,
  direction: 'undo' | 'redo',
): {
  history: EditorHistoryState;
  snapshot: EditorHistorySnapshot | null;
} => {
  const nextIndex =
    direction === 'undo' ? history.index - 1 : history.index + 1;
  if (nextIndex < 0 || nextIndex >= history.entries.length) {
    return { history, snapshot: null };
  }

  return {
    history: { ...history, index: nextIndex },
    snapshot: history.entries[nextIndex] ?? null,
  };
};

export const restoreEditorHistorySnapshot = (
  state: EditorState,
  snapshot: EditorHistorySnapshot,
): EditorState => ({
  ...state,
  undoableState: snapshot.undoableState,
  documentMutationOwner: null,
  timelineMoveActive: false,
  timelineInputOwner: null,
  itemsBeingTrimmed: [],
  activeSnapPoint: null,
  brandTemplateId: snapshot.brandTemplateId,
  editorAppliedAccountBrandTemplateId:
    snapshot.editorAppliedAccountBrandTemplateId,
  selectedItems: filterSelectedItemstoOnlyReturnExistingItems({
    selectedItems: state.selectedItems,
    items: snapshot.undoableState.items,
  }),
});

const onServer = () => false;

export const useUndoRedo = (
  setState: React.Dispatch<React.SetStateAction<EditorState>>,
) => {
  const historyState = useRef<EditorHistoryState>({
    entries: [],
    index: 0,
  });

  const listeners = useRef<(() => void)[]>([]);
  const isHistoryNotificationScheduled = useRef(false);

  const isUndoRedoOperation = useRef(false);

  const notifyHistoryListeners = useCallback(() => {
    // setState updater 내부에서 즉시 알리면 useSyncExternalStore 구독자가 render 중 재평가되어
    // "Cannot update a component while rendering..." 경고가 발생할 수 있다.
    if (isHistoryNotificationScheduled.current) {
      return;
    }

    isHistoryNotificationScheduled.current = true;
    queueMicrotask(() => {
      isHistoryNotificationScheduled.current = false;
      listeners.current.forEach((cb) => cb());
    });
  }, []);

  const pushHistory = useCallback(
    (stateSnapshot: EditorHistorySnapshot) => {
      const nextHistory = pushEditorHistorySnapshot(
        historyState.current,
        stateSnapshot,
      );
      if (nextHistory === historyState.current) {
        return;
      }

      historyState.current = nextHistory;
      notifyHistoryListeners();
    },
    [notifyHistoryListeners],
  );

  /**
   * 현재 undo 포인트의 상태를 새 상태로 교체합니다.
   * 비동기 작업 완료 후 최종 상태를 반영할 때 사용합니다.
   * 새 undo 포인트를 추가하지 않고 기존 항목만 업데이트합니다.
   */
  const replaceCurrentHistory = useCallback(
    (stateSnapshot: EditorHistorySnapshot) => {
      const prevState = historyState.current;
      if (prevState.entries.length === 0) return;

      const newEntries = [...prevState.entries];
      newEntries[prevState.index] = stateSnapshot;

      historyState.current = {
        ...prevState,
        entries: newEntries,
      };
      notifyHistoryListeners();
    },
    [notifyHistoryListeners],
  );

  const undo = useCallback(() => {
    const move = moveEditorHistoryCursor(historyState.current, 'undo');
    const snapshot = move.snapshot;
    if (!snapshot) return;

    isUndoRedoOperation.current = true;
    setState((prev) => restoreEditorHistorySnapshot(prev, snapshot));
    historyState.current = move.history;
    notifyHistoryListeners();
  }, [notifyHistoryListeners, setState]);

  const redo = useCallback(() => {
    const move = moveEditorHistoryCursor(historyState.current, 'redo');
    const snapshot = move.snapshot;
    if (!snapshot) return;

    isUndoRedoOperation.current = true;
    setState((prev) => restoreEditorHistorySnapshot(prev, snapshot));
    historyState.current = move.history;
    notifyHistoryListeners();
  }, [notifyHistoryListeners, setState]);

  const canUndoImperative = useCallback(() => {
    const prevState = historyState.current;
    return prevState.index > 0;
  }, []);

  const canRedoImperative = useCallback(() => {
    const prevState = historyState.current;
    return prevState.index < prevState.entries.length - 1;
  }, []);

  const canRedo = useSyncExternalStore(
    (cb) => {
      listeners.current.push(cb);
      return () => {
        listeners.current = listeners.current.filter((l) => l !== cb);
      };
    },
    canRedoImperative,
    onServer,
  );

  const canUndo = useSyncExternalStore(
    (cb) => {
      listeners.current.push(cb);
      return () => {
        listeners.current = listeners.current.filter((l) => l !== cb);
      };
    },
    canUndoImperative,
    onServer,
  );

  return useMemo(
    () => ({
      undo,
      redo,
      pushHistory,
      replaceCurrentHistory,
      canUndo,
      canRedo,
    }),
    [undo, redo, pushHistory, replaceCurrentHistory, canUndo, canRedo],
  );
};
